import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '@/services/user/userService';
import type { 
  UserProfile, 
  UserProfileUpdate, 
  InviteUserData, 
  Permission, 
  UserRole,
  UserWithPermissions
} from '@/types/user.types';
import { useToast } from '@/hooks/use-toast';

const QUERY_KEYS = {
  users: ['users'],
  user: (id: string) => ['users', id],
  currentUser: ['users', 'current'],
  permissions: (userId?: string) => ['users', 'permissions', userId],
  userWithPermissions: (userId: string) => ['users', userId, 'permissions'],
  invitations: ['invitations'],
  rolePermissions: ['role-permissions'],
} as const;

export function useUsers() {
  return useQuery({
    queryKey: QUERY_KEYS.users,
    queryFn: userService.fetchUsers,
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.user(id),
    queryFn: () => userService.fetchUserById(id),
    enabled: !!id,
  });
}

export function useCurrentUser() {
  return useQuery({
    queryKey: QUERY_KEYS.currentUser,
    queryFn: userService.fetchCurrentUser,
  });
}

export function useUserPermissions(userId?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.permissions(userId),
    queryFn: () => userService.getUserPermissions(userId),
  });
}

export function useUserWithPermissions(userId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.userWithPermissions(userId),
    queryFn: () => userService.fetchUserWithPermissions(userId),
    enabled: !!userId,
  });
}

export function useHasPermission(permission: Permission) {
  return useQuery({
    queryKey: ['permission', permission],
    queryFn: () => userService.hasPermission(permission),
  });
}

export function usePendingInvitations() {
  return useQuery({
    queryKey: QUERY_KEYS.invitations,
    queryFn: userService.fetchPendingInvitations,
  });
}

export function useRolePermissions() {
  return useQuery({
    queryKey: QUERY_KEYS.rolePermissions,
    queryFn: userService.getRolePermissions,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, update }: { id: string; update: UserProfileUpdate }) =>
      userService.updateUserProfile(id, update),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(data.id) });
      toast({
        title: 'User Updated',
        description: 'User profile has been successfully updated.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update user',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUserProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      toast({
        title: 'User Deleted',
        description: 'User has been successfully deleted.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete user',
        variant: 'destructive',
      });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (inviteData: InviteUserData) => userService.inviteUser(inviteData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invitations });
      toast({
        title: 'Invitation Sent',
        description: 'User invitation has been sent successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send invitation',
        variant: 'destructive',
      });
    },
  });
}

export function useCancelInvitation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => userService.cancelInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invitations });
      toast({
        title: 'Invitation Cancelled',
        description: 'Invitation has been cancelled.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to cancel invitation',
        variant: 'destructive',
      });
    },
  });
}